import type { ReactNode } from "react";

import { HubUserAvatar } from "@/components/HubUserAvatar";
import { HoverHintTrigger } from "@/components/ui/title-help";
import type { HubPresenceState } from "@/lib/hubPresenceApi";
import { cn } from "@/lib/utils";

export type HubPresenceAvatarPerson = {
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  presenceState: HubPresenceState;
  tabCount?: number;
};

function displayNameFor(person: HubPresenceAvatarPerson): string {
  const name = person.displayName?.trim();
  return name != null && name !== "" ? name : person.email;
}

function personHint(person: HubPresenceAvatarPerson): string {
  const name = displayNameFor(person);
  const stateLabel = person.presenceState === "active" ? "Active" : "Idle";
  const tabs =
    person.tabCount != null && person.tabCount > 1 ? ` · ${String(person.tabCount)} tabs` : "";
  return `${name} · ${stateLabel}${tabs}`;
}

function personAriaFragment(person: HubPresenceAvatarPerson): string {
  const name = displayNameFor(person);
  const stateLabel = person.presenceState === "active" ? "active" : "idle";
  const tabs =
    person.tabCount != null && person.tabCount > 1 ? `, ${String(person.tabCount)} tabs` : "";
  return `${name} (${stateLabel}${tabs})`;
}

/**
 * Overlapping presence avatars. Active stays full color; idle is grayscale with reduced opacity.
 * Uses HoverHintTrigger — never native `title`.
 */
export function HubPresenceAvatarStack(props: {
  people: HubPresenceAvatarPerson[];
  maxVisible?: number;
  /** Prefix for the group accessible label, for example "Viewing this page". */
  labelPrefix?: string;
  /** Tighter overlap and overflow badge for the header taskbar. */
  compact?: boolean;
  className?: string;
}): ReactNode {
  const { people, maxVisible = 4, labelPrefix = "Viewing this page", compact = false, className } = props;
  if (people.length === 0) {
    return null;
  }

  const visible = people.slice(0, maxVisible);
  const overflow = people.length - visible.length;
  const ariaNames = people.map(personAriaFragment).join(", ");
  const groupLabel =
    people.length === 1
      ? `${labelPrefix}: ${ariaNames}`
      : `${labelPrefix} (${String(people.length)}): ${ariaNames}`;

  const overlapClass = compact ? "-ml-1.5" : "-ml-2";
  const overflowSizeClass = compact
    ? "h-7 w-7 text-[9px]"
    : "h-8 w-8 text-[10px]";

  return (
    <div className={cn("flex items-center", className)} role="group" aria-label={groupLabel}>
      {visible.map((person, index) => {
        const name = displayNameFor(person);
        const idle = person.presenceState === "idle";
        return (
          <HoverHintTrigger
            key={person.userId}
            className={cn(index > 0 && overlapClass)}
            trigger={
              <span
                className={cn(
                  "relative inline-flex rounded-full border-2 border-white shadow-sm ring-1 ring-slate-200",
                  idle && "opacity-60 grayscale",
                )}
                style={{ zIndex: index + 1 }}
                aria-label={personAriaFragment(person)}
              >
                <HubUserAvatar avatarUrl={person.avatarUrl} label={name} size="sm" />
              </span>
            }
          >
            {personHint(person)}
          </HoverHintTrigger>
        );
      })}
      {overflow > 0 ? (
        <HoverHintTrigger
          className={overlapClass}
          trigger={
            <span
              className={cn(
                "relative z-10 inline-flex items-center justify-center rounded-full border-2 border-white bg-slate-100 font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200",
                overflowSizeClass,
              )}
              aria-label={`${String(overflow)} more viewing this page`}
            >
              +{overflow}
            </span>
          }
        >
          {people
            .slice(maxVisible)
            .map((person) => personHint(person))
            .join("; ")}
        </HoverHintTrigger>
      ) : null}
    </div>
  );
}
